<template>
<div>
    <Breadcrumbs main="Admin" title="Skilled Professionals" />
    <!-- Container-fluid starts-->
    <div class="container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-body">
                        <!-- Status tabs -->
                        <div class="d-flex flex-wrap mb-4" style="gap: 8px;">
                            <b-button v-for="tab in tabs" :key="tab.value" size="sm" :variant="activeStatus === tab.value ? 'primary' : 'outline-primary'" @click="changeTab(tab.value)">
                                {{ tab.label }}
                            </b-button>
                        </div>

                        <div v-if="organisationFilter" class="mb-3">
                            <b-badge variant="light" style="font-size: 13px;">
                                Organisation #{{ organisationFilter }}
                                <span style="cursor: pointer; margin-left: 6px;" @click="clearOrganisationFilter">&times;</span>
                            </b-badge>
                        </div>

                        <b-row>
                            <b-col xl="6">
                                <b-input-group class="datatable-btn">
                                    <b-form-input v-model="filter" placeholder="Type to Search"></b-form-input>
                                    <b-input-group-append>
                                        <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>

                            <b-col xl="6">
                                <b-form-group label-cols="2" label="Per page" class="mb-0 datatable-select">
                                    <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div v-if="loading" class="text-center my-4">
                            <b-spinner variant="primary"></b-spinner>
                        </div>
                        <div v-else-if="error" class="text-center my-4 text-danger">
                            Could not load skilled professionals.
                        </div>
                        <div v-else>
                            <div class="table-responsive datatable-vue">
                                <b-table show-empty stacked="md" :items="rows" :fields="tablefields" :filter="filter" :current-page="currentPage" :per-page="perPage" @filtered="onFiltered">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <template #cell(name)="row">
                                        <div style="min-width: 180px; max-width: 280px;">
                                            <router-link :to="`/admin/skilled-professional-view/${row.item.id}`">
                                                <h7>{{ row.item.name || 'N/A' }}</h7>
                                            </router-link>
                                            <div style="color: gray; font-size: 12px;">{{ row.item.email }}</div>
                                        </div>
                                    </template>

                                    <template #cell(organisation_name)="row">
                                        <router-link v-if="row.item.organisation" :to="`/admin/sp-organisation-view/${row.item.organisation.id}`">
                                            {{ row.item.organisation_name }}
                                        </router-link>
                                        <span v-else>—</span>
                                    </template>

                                    <template #cell(roles)="row">
                                        <div class="d-flex flex-wrap" style="gap: 4px; max-width: 240px;">
                                            <b-badge v-for="role in row.item.role_categories" :key="role" variant="info">{{ roleLabel(role, true) }}</b-badge>
                                            <span v-if="!row.item.role_categories || !row.item.role_categories.length">—</span>
                                        </div>
                                    </template>

                                    <template #cell(submitted_at)="row">
                                        {{ formatDate(row.item.submitted_at) }}
                                    </template>

                                    <template #cell(verification_status)="row">
                                        <b-badge :variant="statusVariant(row.item.verification_status)" style="text-transform: capitalize;">{{ row.item.verification_status }}</b-badge>
                                        <div v-if="row.item.conflict" class="mt-1">
                                            <b-badge variant="warning" v-b-tooltip.hover title="Conflict of interest declared">COI</b-badge>
                                        </div>
                                    </template>

                                    <template #cell(action)="data">
                                        <router-link :to="`/admin/skilled-professional-view/${data.item.id}`">
                                            <b-button v-if="data.item.verification_status === 'submitted'" size="sm" variant="primary">Review</b-button>
                                            <feather v-else type="eye" size="22px" style="cursor: pointer;" v-b-tooltip.hover title="View"></feather>
                                        </router-link>
                                    </template>
                                </b-table>
                            </div>
                        </div>

                        <b-col md="6" class="my-1 p-0">
                            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" class="my-0"></b-pagination>
                        </b-col>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Container-fluid Ends-->
</div>
</template>

<script>
import { mapState } from 'vuex';
import { ROLE_LABELS, statusVariant, formatDate } from './skilled-professional-helpers';

export default {
    data() {
        return {
            tabs: [
                { label: 'Submitted', value: 'submitted' },
                { label: 'Verified', value: 'verified' },
                { label: 'Rejected', value: 'rejected' },
                { label: 'Draft', value: 'draft' },
                { label: 'All', value: '' }
            ],
            activeStatus: 'submitted',
            organisationFilter: null,

            tablefields: [
                { key: 'index', label: '#' },
                { key: 'name', label: 'Name', sortable: true },
                { key: 'organisation_name', label: 'Organisation', sortable: true },
                { key: 'roles', label: 'Roles' },
                { key: 'submitted_at', label: 'Submitted', sortable: true },
                { key: 'verification_status', label: 'Status', sortable: true },
                { key: 'action', label: 'Action' }
            ],
            filter: null,
            totalRows: 1,
            currentPage: 1,
            perPage: 10,
            pageOptions: [5, 10, 15, 25]
        };
    },

    computed: {
        ...mapState({
            items: state => state.skilledProfessional.professionals,
            loading: state => state.skilledProfessional.professionalLoading,
            error: state => state.skilledProfessional.professionalError
        }),

        // Flatten nested fields so the table's search and sort work on them
        rows() {
            return (this.items || []).map(item => ({
                ...item,
                name: [item.user?.first_name, item.user?.last_name].filter(Boolean).join(' '),
                email: item.user?.email || '',
                organisation_name: item.organisation?.name || '',
                roles_text: (item.role_categories || []).map(r => this.roleLabel(r)).join(', '),
                conflict: !!(item.conflict_of_interest_disclosure && item.conflict_of_interest_disclosure.trim())
            }));
        }
    },

    watch: {
        '$route.query': {
            handler(query) {
                this.activeStatus = query.status !== undefined ? query.status : 'submitted';
                this.organisationFilter = query.organisation || null;
                this.fetchList();
            },
            immediate: true
        }
    },

    methods: {
        fetchList() {
            this.$store.dispatch('skilledProfessional/fetchProfessionals', {
                verification_status: this.activeStatus,
                organisation: this.organisationFilter
            }).then(() => {
                this.totalRows = this.rows.length;
                this.currentPage = 1;
            });
        },

        changeTab(status) {
            if (status === this.activeStatus) return;
            const query = { ...this.$route.query, status };
            this.$router.replace({ query });
        },

        clearOrganisationFilter() {
            const query = { ...this.$route.query };
            delete query.organisation;
            this.$router.replace({ query });
        },

        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },

        roleLabel(role, short = false) {
            const label = ROLE_LABELS[role];
            if (!label) return role;
            return short ? label.short : label.long;
        },

        statusVariant,
        formatDate
    }
};
</script>
